import React, { useCallback } from 'react';
import * as Styles from './TemplatesList.styles';
import withErrorBoundary from '@/utils/errorBoundary';
import { FORMATS_TO_RENDER_BY_SLUG } from '../../studio.enum';
import { useStudio } from '@/hooks/studio';
import useLocale from '@/hooks/useLocale';
import { Button, Skeleton } from 'gatorcomponents';
import BannerOfferPro from '../BannerOfferPro';
import { useResize } from '@/hooks/useResize';
import ProTag from '../ProTag/ProTag';
import HeaderDesignSection from '../HeaderDesignSection/HeaderDesignSection';
import { studioAnalytics } from '@/analytics/studio';

const TemplatesList = ({
  isProPlan, templates, templatesLoading, handleShowModalGetPro,
}) => {
  const { openStudio } = useStudio();
  const { studio: studioLocale } = useLocale();
  const windowRect = useResize();
  const { width } = windowRect;
  const isMobile = width < 600;

  const handleTemplateClick = useCallback((templateId, isProTemplate) => {
    studioAnalytics.openTemplateClick();

    if ((isProTemplate && isProPlan) || !isProTemplate) {
      openStudio('template', templateId);
    }

    if (isProTemplate && !isProPlan) {
      handleShowModalGetPro && handleShowModalGetPro();
    }
  }, [openStudio, isProPlan, handleShowModalGetPro]);

  const handleShowMoreClick = useCallback(() => {
    studioAnalytics.showMoreTemplatesClick();
    openStudio('dashboard');
  }, [openStudio]);

  const renderTemplates = useCallback(({ start, end }) => FORMATS_TO_RENDER_BY_SLUG.map((formatSlug, index) => {
    if (index >= start && index <= end && templates[formatSlug] && templates[formatSlug].templates.length) {
      return (
        <Styles.FormatList key={formatSlug}>
          <Styles.FormatListHeader>
            <Styles.FormatTitle>
              {studioLocale.templateList.formats[formatSlug] || formatSlug}
            </Styles.FormatTitle>
            <Button
              variant="tertiary"
              onClick={() => openStudio('format', templates[formatSlug].format_id)}
              label={studioLocale.templateList.seeMore}
              size="large"
              version="v2"
            />
          </Styles.FormatListHeader>
          <Styles.TemplateCards>
            {templates[formatSlug] && templates[formatSlug].templates.map(template => (
              <Styles.TemplateCard
                key={template.id}
                onClick={() => handleTemplateClick(template.id, template.pro)}
                format={formatSlug}
              >
                {!isProPlan && template.pro && <ProTag position={{ top: '14px', left: '14px' }} size="small" />}
                <Styles.TemplateImage src={template.thumb} alt={studioLocale.templateList.altText} />
              </Styles.TemplateCard>
            ))}
          </Styles.TemplateCards>
        </Styles.FormatList>
      );
    }
    return null;
  }), [templates, studioLocale.templateList, openStudio, isProPlan, handleTemplateClick]);

  const renderLoadingTemplates = useCallback((quantity, height, width, shape) => (
    <Styles.FormatList>
      <Styles.FormatListHeader>
        <Styles.FormatTitle>
          <Skeleton height="32px" width="483px" variant="rounded" />
        </Styles.FormatTitle>
      </Styles.FormatListHeader>
      <Styles.TemplateCards>
        {Array.from({ length: quantity }).map(() => (
          <Skeleton
            height={height}
            width={width}
            variant={shape}
            key={Math.random().toString(36).substr(2, 9)}
          />
        ))}
      </Styles.TemplateCards>
    </Styles.FormatList>
  ), []);

  if (!templates.length && templatesLoading) {
    return (
      <Styles.Wrapper
        data-testid="top-banner-wrapper"
        isProPlan={isProPlan}
      >
        {renderLoadingTemplates(6, '200px', '200px', 'rectangle')}
        {renderLoadingTemplates(6, '314px', '200px', 'rectangle')}
        <Skeleton width="1280px" height="400px" variant="rectangle" />
        {renderLoadingTemplates(4, '200px', '308px', 'rectangle')}
        {renderLoadingTemplates(6, '200px', '200px', 'rectangle')}
        {renderLoadingTemplates(6, '314px', '200px', 'rectangle')}
        {renderLoadingTemplates(4, '144px', '308px', 'rectangle')}
        {renderLoadingTemplates(6, '200px', '200px', 'circle')}
        <Styles.ButtonWrapper>
          <Skeleton variant="button" />
        </Styles.ButtonWrapper>
      </Styles.Wrapper>
    );
  }

  return (
    <Styles.Wrapper
      data-testid="top-banner-wrapper"
      isProPlan={isProPlan}
    >
      <HeaderDesignSection isProPlan={isProPlan} />
      {renderTemplates({ start: 0, end: 1 })}

      {!isProPlan && (
        <BannerOfferPro
          handleShowModalGetPro={handleShowModalGetPro}
        />
      )}

      {renderTemplates({ start: 2, end: 9 })}

      <Styles.ButtonWrapper>
        <Button
          label={studioLocale.templateList.showMoreTemplates}
          onClick={() => handleShowMoreClick()}
          version="v2"
          size="large"
          width={isMobile ? 'full' : 'fit'}
        />
      </Styles.ButtonWrapper>
    </Styles.Wrapper>
  );
};
export default withErrorBoundary(TemplatesList, 'TemplatesList');
